/* eslint-disable react/no-unknown-property */
'use client';

import React, { useEffect, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import {
  Box,
  Button,
  Modal,
  Typography,
  useMediaQuery,
  useTheme as useMuiTheme,
  Fade,
  Zoom,
} from '@mui/material';
import PromotionModalCard from './PromotionModalCard';
import CloseIcon from '@mui/icons-material/Close';
import { getModalPromotions } from '@/services/promotion-api';
import PromotionLoader from './PromotionLoader';
import { useTheme } from '@/context/ThemeContext';
import { useLanguage } from '@/context/LanguageContext';
import { useIntl } from '@/utils/intl';
import { resolveImageUrl } from '@/utils/constants';
import { useToast } from '@/context/ToastContext';

const PromotionModal = () => {
  const muiTheme = useMuiTheme();
  const { theme } = useTheme();
  const router = useRouter();
  const { locale } = useLanguage();
  const { translate } = useIntl();
  const { showError } = useToast();
  const pathname = usePathname()

  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [modalData, setModalData] = useState<{
    heroPromotion?: any | null;
    carouselPromotions: any[];
    settings: {
      showManyTimes?: boolean | null;
      modalTitleEn?: string | null;
      modalTitleMn?: string | null;
      popUpShowTime?: number | null;
    };
  } | null>(null);

  const carouselRef = useRef<HTMLDivElement>(null);
  const autoplayRef = useRef<NodeJS.Timeout | null>(null);
  const wheelTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pointerStartX = useRef<number | null>(null);
  const pointerStartY = useRef<number | null>(null);
  const [manualControl, setManualControl] = useState(false);
  const [isScrollPaused, setIsScrollPaused] = useState(false);
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('md'));

  const handleNavigate = (url: string) => {
    if (modalData && !modalData.settings.showManyTimes) {
      localStorage.setItem('promotionModalShown', 'true');
    }
    setOpen(false);
    router.push(url);
  };


  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setOpen(false);
      setIsClosing(false);

      if (modalData && !modalData.settings.showManyTimes) {
        localStorage.setItem('promotionModalShown', 'true');
      }
    }, 300); // allows fade/zoom animation
  };

  const handlePrev = () => {
    const length = modalData?.carouselPromotions?.length || 0;
    if (length === 0) return;

    setCurrentIndex((prev) => (prev === 0 ? length - 1 : prev - 1));
  };

  const handleNext = () => {
    const length = modalData?.carouselPromotions?.length || 0;
    if (length === 0) return;

    setCurrentIndex((prev) => (prev === length - 1 ? 0 : prev + 1));
  };

  const getVisibleCards = () => {
    const cards: any[] = [];
    const carouselItems = modalData?.carouselPromotions || [];
    const length = carouselItems.length;

    if (length === 0) return cards;

    if (isMobile) {
      cards.push({
        ...carouselItems[currentIndex],
        offset: 0,
      });
    } else {
      if (length === 1) {
        cards.push({ ...carouselItems[0], offset: 0 });
      } else if (length === 2) {
        cards.push({ ...carouselItems[currentIndex], offset: 0 });
        const nextIndex = (currentIndex + 1) % length;
        cards.push({ ...carouselItems[nextIndex], offset: 1 });
      } else {
        for (let i = -1; i <= 1; i++) {
          const index = (currentIndex + i + length) % length;
          cards.push({ ...carouselItems[index], offset: i });
        }
      }
    }
    return cards;
  };
  useEffect(() => {
    if (!open || isHovered || !modalData?.carouselPromotions?.length) return;

    const autoplayInterval = setInterval(() => {
      handleNext();
    }, 3000);

    return () => clearInterval(autoplayInterval);
  }, [open, isHovered, currentIndex, modalData?.carouselPromotions?.length]);

  useEffect(() => {
    const asyncFunc = async () => {
      setIsLoading(true);
      try {
        const data = await getModalPromotions();
        setModalData({
          heroPromotion: data?.heroPromotion,
          carouselPromotions: data?.carouselPromotions,
          settings: data?.settings,
        });

        const hasHeroPromotion = !!data?.heroPromotion;
        const hasCarouselPromotions = data?.carouselPromotions?.length > 0;
        const hasShownBefore = localStorage.getItem('promotionModalShown');
        const isVip = pathname?.includes('vip-shift')
        const isHome = pathname === '/'

        if (isVip || !isHome) return setIsLoading(false);

        if ((hasHeroPromotion || hasCarouselPromotions) &&
          (data?.settings?.showManyTimes || !hasShownBefore)) {
          setOpen(true);
        }
      } catch (error: any) {
        console.error('Error:', error);
        if (error?.message) showError(error.message);
      }
      setIsLoading(false);
    };
    asyncFunc();
  }, []);



  useEffect(() => {
    if (!open || !modalData?.settings?.popUpShowTime) return;
    const timeoutId = setTimeout(() => {
      handleClose();
    }, modalData.settings.popUpShowTime * 1000);
    return () => clearTimeout(timeoutId);
  }, [open, modalData?.settings?.popUpShowTime]);

  useEffect(() => {
    if (!open) return;

    if (manualControl || isHovered || isScrollPaused) {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
        autoplayRef.current = null;
      }
      return;
    }

    autoplayRef.current = setInterval(() => {
      handleNext();
    }, 3000);

    return () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
      }
    };
  }, [open, manualControl, isHovered, isScrollPaused]);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();

      const delta =
        Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;

      if (Math.abs(delta) < 10) return;

      setManualControl(true);
      setIsScrollPaused(true);

      if (delta > 0) {
        handleNext();
      } else {
        handlePrev();
      }

      if (wheelTimeoutRef.current) {
        clearTimeout(wheelTimeoutRef.current);
      }

      wheelTimeoutRef.current = setTimeout(() => {
        setIsScrollPaused(false);
        setManualControl(false);
      }, 3000);
    };

    el.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      el.removeEventListener('wheel', onWheel);
      if (wheelTimeoutRef.current) {
        clearTimeout(wheelTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;

    const onPointerDown = (e: PointerEvent) => {
      pointerStartX.current = e.clientX;
      pointerStartY.current = e.clientY;

      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    };

    const onPointerUp = (e: PointerEvent) => {
      if (pointerStartX.current === null) return;

      const dx = e.clientX - pointerStartX.current;

      if (Math.abs(dx) > 40) {
        setManualControl(true);
        setIsScrollPaused(true);

        if (dx < 0) {
          handleNext();
        } else {
          handlePrev();
        }

        if (wheelTimeoutRef.current) {
          clearTimeout(wheelTimeoutRef.current);
        }

        wheelTimeoutRef.current = setTimeout(() => {
          setIsScrollPaused(false);
          setManualControl(false);
        }, 3000);
      }

      pointerStartX.current = null;
      pointerStartY.current = null;
    };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerUp);

    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
    };
  }, []);

  if (isLoading || !modalData) {
    return (
      <div
        className='w-full min-h-screen flex items-center justify-center'
        style={{
          backgroundColor: theme === 'dark' ? 'rgba(29,37,45,1)' : 'white',
        }}
      >
        <PromotionLoader />
      </div>
    );
  }
  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby='promotion-modal'
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& .MuiModal-backdrop': {
          outline: 'none',
        },
        '&:focus-visible': {
          outline: 'none',
        },
        '&:focus': {
          outline: 'none !important',
        },
        '& .MuiBox-root:focus': {
          outline: 'none !important',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(8px)',
            outline: 'none',
          },
        },
      }}
      closeAfterTransition
    >
      <Fade in={open && !isClosing} timeout={400}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            px: { xs: '20px' },
            pointerEvents: 'none',
          }}
        >
          <Zoom
            in={open && !isClosing}
            timeout={500}
            style={{ transitionDelay: '100ms' }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: { xs: '100%', sm: '80%', md: '900px' },
                height: { xs: 'auto', sm: 'auto' },
                maxHeight: { xs: '85vh', sm: '90vh' },
                px: { xs: '20px', sm: '20px' },
                pt: { xs: '20px', sm: '20px' },
                backgroundColor:
                  theme === 'dark' ? 'rgba(29,37,45,1)' : 'white',
                borderRadius: '12px',
                outline: 'none !important',
                border: 'none !important',
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'center', sm: 'center' },
                justifyContent: { xs: 'center', sm: 'center' },
                overflow: 'hidden',
                boxShadow: '0 24px 48px rgba(0, 0, 0, 0.4)',
                pointerEvents: 'auto',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  mx: 'auto',
                  borderRadius: { xs: '8px', sm: '10px', md: '12px' },
                  overflow: 'hidden',
                  boxShadow: '0 0 60px rgba(0, 0, 0, 0.5)',
                  animation: 'scaleIn 0.7s ease-out 0.2s backwards',
                  paddingBottom: { xs: '8px', sm: 0, md: 0 },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '50%',
                    background:
                      'linear-gradient(to top, rgba(0, 0, 0, 0.90), transparent)',
                    pointerEvents: 'none',
                    zIndex: 1,
                  },
                }}
              >
                <Box
                  component='img'
                  src={
                    modalData?.heroPromotion?.imgHorizontalUrlMn ||
                      modalData?.heroPromotion?.imgHorizontalUrlEn
                      ? locale === 'mn'
                        ? resolveImageUrl(modalData?.heroPromotion?.imgHorizontalUrlMn)
                        : resolveImageUrl(modalData?.heroPromotion?.imgHorizontalUrlEn)
                      : undefined
                  }
                  alt={
                    locale === 'mn'
                      ? modalData?.heroPromotion?.titleMn
                      : modalData?.heroPromotion?.titleEn || ''
                  }
                  draggable={false}
                  sx={{
                    width: '100%',
                    aspectRatio: '2/1',
                    objectFit: 'cover',
                    borderRadius: { xs: '8px', sm: '10px', md: '12px' },
                  }}
                />
                <CloseIcon
                  onClick={handleClose}
                  className="
                    absolute z-20
                    bg-[#f2f2f2] dark:bg-[#2a2a2a]
                    text-[#222] dark:text-[#f5f5f5]
                    p-2
                    rounded-xl
                    cursor-pointer
                    transition-all duration-200
                    hover:scale-110 hover:rotate-90
                    hover:bg-[#e8e8e8] dark:hover:bg-[#333]
                  "
                  style={{
                    width: isMobile ? '36px' : '40px',
                    height: isMobile ? '36px' : '40px',
                    top: '12px',
                    right: '12px',
                  }}
                />
                <Button
                  variant='contained'
                  sx={{
                    display: { xs: 'none', sm: 'block', md: 'block' },
                    position: 'absolute',
                    backgroundColor: '#EF4444 !important',
                    color: 'white',
                    top: 'auto',
                    bottom: { sm: 20, md: 30 },
                    left: 'auto',
                    right: { sm: '20px', md: '30px' },
                    px: { sm: '15px' },
                    fontSize: { sm: '14px', md: '15px' },
                    fontWeight: 400,
                    textTransform: 'none',
                    borderRadius: '12px',
                    paddingRight: '16px',
                    paddingLeft: '16px',
                    height: '40px',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                    transition: 'all 0.3s ease',
                    zIndex: 1000,
                    '&:hover': {
                      backgroundColor: '#DC2626',
                      transform: {
                        sm: 'scale(1.05)',
                        md: 'scale(1.05)',
                      },
                      boxShadow: '0 12px 32px rgba(0, 0, 0, 0.4)',
                    },
                  }}
                  onClick={() =>
                    handleNavigate(`/promotion/${modalData?.heroPromotion?._id}`)
                  }
                >
                  {translate('promotions.buy')}
                </Button>
                <Typography
                  variant='h5'
                  component='h2'
                  sx={{
                    position: 'absolute',
                    bottom: { xs: 10, sm: 20, md: 30 },
                    left: '50%',
                    transform: 'translateX(-50%)',
                    textAlign: 'center',
                    textWrap: 'wrap',
                    fontWeight: 700,
                    fontSize: '26px',
                    color: 'white',
                    maxWidth: 600,
                    animation: 'slideDown 0.6s ease-out',
                    zIndex: 1000,
                  }}
                >
                  {locale === 'mn'
                    ? modalData?.settings?.modalTitleMn
                    : modalData?.settings?.modalTitleEn || ''}
                </Typography>
              </Box>
              <Button
                variant='contained'
                sx={{
                  display: { xs: 'block', sm: 'none', md: 'none' },
                  backgroundColor: '#EF4444 !important',
                  color: 'white',
                  mt: 2,
                  px: '12px',
                  py: '8px',
                  fontSize: '13px',
                  fontWeight: 400,
                  textTransform: 'none',
                  borderRadius: '12px',
                  paddingRight: '16px',
                  paddingLeft: '16px',
                  height: '40px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: '#DC2626',
                    transform: 'scale(1.05)',
                    boxShadow: '0 12px 32px rgba(0, 0, 0, 0.4)',
                  },
                }}
                onClick={() =>
                  handleNavigate(`/promotion/${modalData?.heroPromotion?._id}`)
                }
              >
                {translate('promotions.buy')}
              </Button>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  animation: 'scaleIn 0.7s ease-out 0.2s backwards',
                }}
              >
                <Box
                  ref={carouselRef}
                  sx={{
                    position: 'relative',
                    minHeight: { xs: 160, sm: 160, md: 180 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    animation: 'fadeInUp 0.8s ease-out 0.3s backwards',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'visible',
                    }}
                  >
                    {getVisibleCards().map((card, index) => {
                      const scale = 1;
                      const opacity = 1;
                      const zIndex = 10;
                      const cardWidth = isMobile ? 240 : isTablet ? 240 : 280;
                      const cardGap = isMobile ? 10 : isTablet ? 10 : 10;
                      const cardSpacing = cardWidth + cardGap;
                      const translateX = card.offset * cardSpacing;
                      return (
                        <Box
                          key={card._id || index}
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'absolute',
                            transition: 'all 0.5s ease-out',
                            transform: `translateX(${translateX}px) scale(${scale})`,
                            opacity: opacity,
                            zIndex: zIndex,
                          }}
                        >
                          <PromotionModalCard
                            id={card._id}
                            title={
                              locale === 'mn'
                                ? card.titleMn
                                : card.titleEn || ''
                            }
                            img={
                              (locale === 'mn'
                                ? card.imgHorizontalUrlMn
                                : card.imgHorizontalUrlEn) || undefined
                            }
                            onNavigate={(id) => handleNavigate(`/promotion/${id}`)}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
              <style jsx global>{`
                @keyframes slideDown {
                  from {
                    opacity: 0;
                    transform: translateY(-30px);
                  }
                  to {
                    opacity: 1;
                    transform: translateY(0);
                  }
                }

                @keyframes scaleIn {
                  from {
                    opacity: 0;
                    transform: scale(0.9);
                  }
                  to {
                    opacity: 1;
                    transform: scale(1);
                  }
                }

                @keyframes fadeInUp {
                  from {
                    opacity: 0;
                    transform: translateY(20px);
                  }
                  to {
                    opacity: 1;
                    transform: translateY(0);
                  }
                }

                @keyframes bounce {
                  0%,
                  100% {
                    transform: translateY(0);
                  }
                  50% {
                    transform: translateY(-10px);
                  }
                }
              `}</style>
            </Box>
          </Zoom>
        </Box>
      </Fade>
    </Modal>
  );
};

export default PromotionModal;